"use client";

import type { PortalReviewsResponse } from "../lib/clientPortalApi";
import { PortalReviewRating, PortalReviewStar, portalRatingLabel } from "./PortalReviewRating";
import styles from "./PortalReviewFilters.module.css";

type PortalReviewFiltersProps = {
  averageRating: number | null;
  total: number;
  ratingCounts: PortalReviewsResponse["ratingCounts"];
  selectedRatings: number[];
  disabled: boolean;
  onChange: (ratings: number[]) => void;
};

const ratings = [5, 4, 3, 2, 1] as const;
const numberFormat = new Intl.NumberFormat("cs", { maximumFractionDigits: 1 });

function reviewCountLabel(count: number) {
  return `${numberFormat.format(count)} ${count >= 1 && count <= 4 ? "recenze" : "recenzí"}`;
}

export function PortalReviewFilters({ averageRating, total, ratingCounts, selectedRatings, disabled, onChange }: PortalReviewFiltersProps) {
  function toggle(rating: number, selected: boolean) {
    onChange(selected
      ? [...selectedRatings, rating].sort((left, right) => right - left)
      : selectedRatings.filter((value) => value !== rating));
  }

  return <div className={styles.panel}>
    <div className={styles.summary}>
      <div className={styles.average} aria-hidden="true">{averageRating === null ? "—" : numberFormat.format(averageRating)}<span>/ 5</span></div>
      <div className={styles.summaryDetails}>
        <PortalReviewRating rating={averageRating ?? 0} decorative={total === 0} />
        <span className={styles.count}>{reviewCountLabel(total)}</span>
      </div>
    </div>
    <fieldset className={styles.filters} disabled={disabled}>
      <legend className={styles.visuallyHidden}>Filtrovat podle hodnocení</legend>
      {ratings.map((rating) => {
        const count = ratingCounts[String(rating) as keyof PortalReviewsResponse["ratingCounts"]];
        return <label key={rating} className={styles.filter} data-selected={selectedRatings.includes(rating)}>
          <input
            className={styles.visuallyHidden}
            type="checkbox"
            checked={selectedRatings.includes(rating)}
            onChange={(event) => toggle(rating, event.target.checked)}
            aria-label={`${portalRatingLabel(rating)}, ${reviewCountLabel(count)}`}
          />
          <span className={styles.checkbox} aria-hidden="true"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 8 3 3 7-7" /></svg></span>
          <span className={styles.rating} aria-hidden="true">{rating}<PortalReviewStar /></span>
          <span className={styles.bar} aria-hidden="true"><span style={{ width: `${total > 0 ? Math.min(100, count / total * 100) : 0}%` }} /></span>
          <span className={styles.ratingCount} aria-hidden="true">{numberFormat.format(count)}</span>
        </label>;
      })}
      {selectedRatings.length > 0 ? <button type="button" className={styles.reset} onClick={() => onChange([])}>Zrušit filtr</button> : null}
    </fieldset>
  </div>;
}
